<template>
    <b-container class="login-container without-spacing">
        <h2 class="header-bottom-margin">{{ $t('title') }}</h2>

        <div v-if="isFormVisible">
            <b-alert fade v-model="message.visible" :variant="message.type" dismissible class="alert">
                {{ message.text }}
            </b-alert>

            <p>{{ $t('subtitle') }}</p>

            <validation-observer ref="validation-observer" v-slot="{ handleSubmit }">
                <b-form @submit.prevent="handleSubmit(login)">
                    <validation-provider name="email"
                                         :rules="usedAdAuth ? 'required' : 'required|email'"
                                         v-slot="validationContext">
                        <b-form-group id="user-email-group"
                                      :label="usedAdAuth ? $t('user-ad-name-label') : $t('user-email-label')"
                                      label-for="user-email"
                                      :invalid-feedback="validationContext.errors[0]">
                            <b-form-input id="user-email"
                                          v-model="user.email"
                                          :state="getValidationState(validationContext)"
                                          :disabled="actionsDisabled">
                            </b-form-input>
                        </b-form-group>
                    </validation-provider>

                    <validation-provider name="password"
                                         rules="required|min:8"
                                         v-slot="validationContext">
                        <b-form-group id="user-password-group" label-for="user-password"
                                      :invalid-feedback="validationContext.errors[0]">
                            <template v-slot:label>
                                {{ $t('user-password-label') }}
                                <b-icon v-if="isVisiblePassword" @click="isVisiblePassword = !isVisiblePassword"
                                        icon="eye-slash-fill"
                                        class="password-visibility-icon"></b-icon>
                                <b-icon v-else icon="eye-fill" @click="isVisiblePassword = !isVisiblePassword"
                                        class="password-visibility-icon"></b-icon>
                            </template>
                            <b-form-input id="user-password"
                                          v-model="user.password"
                                          :type="passwordFieldType"
                                          :state="getValidationState(validationContext)"
                                          :disabled="actionsDisabled">
                            </b-form-input>
                        </b-form-group>
                    </validation-provider>

                    <template v-if="usedAdAuth">
                        <dp-button class="ad-login-btn" type="submit" :disabled="actionsDisabled">
                            {{ $t('login-action') }}
                        </dp-button>
                    </template>
                    <template v-else>
                        <div class="password-recovery">
                            <b-link :to="{name: 'recovery'}">{{ $t('forget-password') }}</b-link>
                        </div>

                        <dp-button type="submit" :disabled="actionsDisabled">{{ $t('login-action') }}</dp-button>
                        <label class="action-divider">{{ $t('action-divider') }}</label>
                        <dp-button ghost @click.prevent="goToRegistration">
                            {{ $t('register-action') }}
                        </dp-button>
                    </template>
                </b-form>
            </validation-observer>
        </div>
    </b-container>
</template>

<script>
import DpButton from '@/components/button/dp-button'
import ValidationMixin from '@/mixins/validation-mixin'
import ErrorMixin from '@/mixins/error-mixin'
import UserService from '@/services/user-service'
import ClientService from '@/services/client-service'

export default {
  name: 'login',
  components: {
    DpButton
  },
  mixins: [ValidationMixin, ErrorMixin],
  data() {
    return {
      isFormVisible: false,
      actionsDisabled: false,
      isVisiblePassword: false,
      user: {
        email: null,
        password: null
      },
      message: {
        visible: false,
        type: 'danger',
        text: null
      }
    }
  },
  computed: {
    passwordFieldType() {
      return this.isVisiblePassword ? 'text' : 'password'
    },
    redirectUrl() {
      return this.$store.getters.getClient.redirectUrl
    },
    usedAdAuth() {
      return process.env.VUE_APP_USE_AD_AUTH === 'true'
    }
  },
  mounted() {
    this.validateAuth()
  },
  methods: {
    login() {
      this.message.visible = false
      this.actionsDisabled = true

      UserService.login(this.user.email, this.user.password, this.$i18n.locale).then((result) => {
        this.$store.dispatch('setAuthToken', result.data.auth_token)
        if (this.redirectUrl) {
          window.location.replace(`${this.redirectUrl}?authorization_code=${result.data.authorization_code}`)
        } else {
          window.location.replace(`${process.env.VUE_APP_DC_URL}?authorization_code=${result.data.authorization_code}`)
          // this.$router.replace({name: 'profile'})
        }
        // window.location.replace(`${process.env.VUE_APP_DC_URL}?authorization_code=${result.data.authorization_code}`)
      }).catch((error) => {
        this.message.text = this.getErrorMessage(error)
        this.message.type = 'danger'
        this.message.visible = true
      }).finally(() => {
        this.actionsDisabled = false
      })
    },
    validateAuth() {
      if (!this.$store.getters.getAuthToken) {
        this.isFormVisible = true
        return
      }

      ClientService.validateAuth(this.$store.getters.getClient.clientId).then((result) => {
        if (result.data.authorization_code) {
          if (this.redirectUrl) {
            window.location.replace(`${this.redirectUrl}?authorization_code=${result.data.authorization_code}`)
          } else {
            window.location.replace(`${process.env.VUE_APP_DC_URL}?authorization_code=${result.data.authorization_code}`)
            // this.$router.replace({name: 'profile'})
          }
          // window.location.replace(`${process.env.VUE_APP_DC_URL}?authorization_code=${result.data.authorization_code}`)
        } else {
          this.isFormVisible = true
        }
      }).catch(() => {
        this.isFormVisible = true
      })
    },
    goToRegistration() {
      this.$router.push({
        name: 'registration'
      })
    }
  }
}
</script>

<style lang="scss">
.login-container {
  .alert {
    width: fit-content;
    min-width: 282px;
  }

  .password-recovery {
    margin-top: 40px !important;
    margin-bottom: 32px !important;

    a {
      font-weight: $--font-weight-light !important;
      font-size: $--font-size-small !important;
      line-height: 21px;
    }
  }

  .action-divider {
    margin-left: 14px;
    margin-right: 14px;
    font-weight: $--font-weight-medium;
  }

  .password-visibility-icon {
    float: right;
    font-size: $--font-size-medium-base;
    cursor: pointer;
  }

  form {
    margin-top: 60px;
  }

  .ad-login-btn {
    margin-top: 32px;
  }
}
</style>

<i18n>
{
    "en": {
        "title": "Log In",
        "subtitle": "Enter your email address and password to log in.",
        "forget-password": "Don’t remember your password?"
    },
    "ru": {
        "title": "Войти",
        "subtitle": "Введите электронную почту и пароль, чтобы войти в систему.",
        "forget-password": "Забыли пароль?"
    }
}
</i18n>
